/**
 * note.sticky adapter — ClientAdapter for note.sticky tiles.
 *
 * note.sticky is a noFetch, clientDirect tile. Params ARE the storage:
 * { text: string } is the user's note body, edited via configureTile only.
 * Data is computed locally from params (no network, no upstream).
 *
 * GLANCE RULINGS (glance-grammar-spec §1.3 / D-015):
 *   primary : preview (first ~80 chars, newlines→spaces).
 *             EMPTY-STATE exception: when text === '' (fresh note or user
 *             cleared it), primary shows 'Tap to write' as an invitation —
 *             this is a COMPUTED value in glanceView, NOT stored. The stored
 *             preview is '' (the raw empty-note data), and glanceView maps
 *             '' → 'Tap to wri' (clamped to 8 chars). We use 'Empty' (5 chars)
 *             as the empty-state primary so it fits within the 8-char limit and
 *             reads calm on a 1×1 tile.
 *             Decision: use 'Empty' (5 chars) — fits 1×1 comfortably, signals
 *             actionability without being pushy ("Tap to write" is 12 chars, over
 *             the 8-char primary cap). A fresh note shows 'Empty' in the glance
 *             slot; the detail sheet textarea invites writing.
 *   delta   : null (no trend/secondary fact for a note; the body is the content)
 *   state   : null (neutral — a note has no positive/negative semantic)
 *
 * Immutability: the exported adapter object is frozen; glanceView returns new.
 */
import { z } from 'zod';
import {
  noteParamsSchema,
  deriveCacheKey,
  type NoteParams,
} from 'briefpane/schema';
import type { GlanceMap } from 'briefpane/schema';
import type { ClientAdapter, GlanceViewModel } from 'briefpane/contracts';
import { computeNoteData, type NoteData } from './compute.js';

/** Zod shape for NoteData — mirrors the fields NoteData declares. */
export const noteDataShape = z.object({
  preview: z.string(),
  charCount: z.number().int().min(0),
});

/**
 * Map NoteData + GlanceMap → GlanceViewModel.
 *
 * Empty-state ruling: preview === '' → primary = 'Empty' (5 chars, fits 8-char
 * limit). Non-empty: primary = preview.slice(0,8) via the central clamp.
 * delta is always null. glanceState is always neutral.
 */
export function noteGlanceView(data: NoteData, _glance: GlanceMap): GlanceViewModel {
  const primary = data.preview === '' ? 'Empty' : data.preview;
  return {
    dataState: 'ok',
    glanceState: 'neutral',
    slots: { primary, delta: null },
    label: null,
    fetchedAt: Date.now(),
  };
}

export const noteAdapter: ClientAdapter<NoteParams, NoteData> = Object.freeze({
  type: 'note.sticky' as const,
  provider: 'local' as const,
  tier: 'free' as const,
  ttlSec: 0,
  paramsSchema: noteParamsSchema,
  dataShape: noteDataShape,

  cacheKey: (params: NoteParams): string =>
    deriveCacheKey({ type: 'note.sticky', provider: 'local', params }),

  glanceView: (data: NoteData, glance: GlanceMap): GlanceViewModel =>
    noteGlanceView(data, glance),
});

/** Compute NoteData from NoteParams (exported for use-tile-view-model). */
export { computeNoteData };
